import { useId, type CSSProperties, type MouseEventHandler, type ReactNode, type Ref } from 'react'
import { Radio as AntRadio } from 'antd'
import type { RadioChangeEvent, RadioRef as AntRadioRef } from 'antd/es/radio/interface'

export interface RadioProps {
  value?: unknown
  checked?: boolean
  onChange?: (e: RadioChangeEvent) => void
  onClick?: MouseEventHandler<HTMLElement>
  disabled?: boolean
  label?: ReactNode
  children?: ReactNode
  id?: string
  name?: string
  className?: string
  style?: CSSProperties
  autoFocus?: boolean
  tabIndex?: number
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

function RadioBase({
  ref,
  value,
  checked,
  onChange,
  onClick,
  disabled,
  label,
  children,
  id: idProp,
  name,
  className,
  autoFocus,
  'aria-label': ariaLabel,
  ...rest
}: RadioProps & { ref?: Ref<AntRadioRef> }) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const accessibleName = ariaLabel ?? (typeof label === 'string' ? label : undefined)
  const mergedClassName = className ? `glasspro-radio ${className}` : 'glasspro-radio'
  return (
    <AntRadio
      ref={ref}
      id={id}
      name={name ?? id}
      value={value}
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={accessibleName}
      {...rest}
      className={mergedClassName}
    >
      {label ?? children}
    </AntRadio>
  )
}

export const Radio = Object.assign(RadioBase, {
  Group: AntRadio.Group,
  Button: AntRadio.Button,
})
